import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "../config/testimonials";

const AUTO_INTERVAL = 7000;

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % TESTIMONIALS.length);
    }, AUTO_INTERVAL);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused]);

  const go = (n) => {
    setIdx((i) => (i + n + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const active = TESTIMONIALS[idx];

  return (
    <section
      className="site-container py-14 lg:py-20"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mb-9 flex flex-col md:flex-row items-start md:items-end justify-between gap-4 reveal">
        <div>
          <div className="eyebrow mb-4 text-[#5FA637]">Stimmen aus Projekten</div>
          <h2 className="section-title max-w-3xl">
            Was Partner <span className="italic-accent">über uns sagen.</span>
          </h2>
          <p className="mt-3 text-xs text-white/40 italic">
            * Platzhalter — werden durch freigegebene Kundenstimmen ersetzt.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0" data-testid="testimonials-nav">
          <button
            onClick={() => go(-1)}
            data-testid="testimonial-prev"
            aria-label="Vorherige Stimme"
            className="w-11 h-11 border border-white/20 hover:border-[#5FA637] hover:text-[#5FA637] flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={16}/>
          </button>
          <button
            onClick={() => go(1)}
            data-testid="testimonial-next"
            aria-label="Nächste Stimme"
            className="w-11 h-11 border border-white/20 hover:border-[#5FA637] hover:text-[#5FA637] flex items-center justify-center transition-colors"
          >
            <ChevronRight size={16}/>
          </button>
        </div>
      </div>

      <div className="relative surface-panel overflow-hidden" data-testid="testimonial-card">
        {/* Decorative quote mark */}
        <Quote size={120} strokeWidth={1} className="absolute -top-6 -right-6 text-[#5FA637]/10 pointer-events-none"/>

        <div className="relative p-8 md:p-12 min-h-[260px] flex flex-col justify-between">
          <blockquote
            key={idx}
            className="font-display text-xl md:text-2xl lg:text-3xl leading-[1.35] max-w-4xl animate-fade-up"
            data-testid="testimonial-quote"
          >
            <span className="italic-accent text-[#5FA637]/70 mr-2">„</span>
            {active.quote}
            <span className="italic-accent text-[#5FA637]/70 ml-1">"</span>
          </blockquote>

          <div className="mt-10 flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 border border-[#5FA637]/40 bg-[#5FA637]/10 text-[#5FA637] flex items-center justify-center font-display text-lg font-bold shrink-0">
                {active.initials}
              </div>
              <div className="leading-tight">
                <div className="text-white font-medium text-base" data-testid="testimonial-role">{active.role}</div>
                <div className="text-white/50 text-xs mt-1 uppercase tracking-[0.15em]">{active.location}</div>
              </div>
            </div>

            {/* Pagination dots */}
            <div className="flex items-center gap-2" data-testid="testimonial-dots">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Stimme ${i + 1} anzeigen`}
                  className={`h-1 transition-all ${i === idx ? "w-10 bg-[#5FA637]" : "w-5 bg-white/20 hover:bg-white/40"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </section>
  );
}
